import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import {
  Grid,
  Paper,
  Typography,
  Container,
  makeStyles,
  CircularProgress,
  Box,
  Tab,
  Tabs
} from '@material-ui/core';
import { 
  LocalFlorist,
  Warning,
  CheckCircle,
  Schedule,
  Assessment
} from '@material-ui/icons';
import { MuiPickersUtilsProvider } from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';
import axios from 'axios';
import { socket } from '../../SocketIo/Socket';

// Import components (to be created)
import PlantTrackingTable from './PlantTrackingTable';
import PlantTrackingMap from './PlantTrackingMap';
import PlantDetailsModal from './PlantDetailsModal';
import PlantFilters from './PlantFilters';
import PlantPhotoGallery from './PlantPhotoGallery';
import PlantAnalytics from './PlantAnalytics';

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
    padding: theme.spacing(3)
  },
  paper: {
    padding: theme.spacing(2),
    textAlign: 'center',
    color: theme.palette.text.secondary,
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center'
  },
  statCard: {
    padding: theme.spacing(3),
    textAlign: 'center',
    borderRadius: theme.spacing(1),
    cursor: 'pointer',
    transition: 'transform 0.2s, box-shadow 0.2s',
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: theme.shadows[8]
    }
  },
  statIcon: {
    fontSize: 48,
    marginBottom: theme.spacing(1)
  },
  statValue: {
    fontSize: '2rem',
    fontWeight: 'bold',
    color: theme.palette.text.primary
  },
  statLabel: {
    fontSize: '0.875rem',
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5)
  },
  deadPlantCard: {
    backgroundColor: '#ffebee',
    '& .MuiSvgIcon-root': {
      color: '#d32f2f'
    }
  },
  dyingPlantCard: {
    backgroundColor: '#fff3e0',
    '& .MuiSvgIcon-root': {
      color: '#f57c00'
    }
  },
  completedCard: {
    backgroundColor: '#e8f5e9',
    '& .MuiSvgIcon-root': {
      color: '#388e3c'
    }
  },
  pendingCard: {
    backgroundColor: '#e3f2fd',
    '& .MuiSvgIcon-root': {
      color: '#1976d2'
    }
  },
  tabsContainer: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(3)
  },
  filterContainer: {
    marginBottom: theme.spacing(2),
    backgroundColor: '#f5f5f5',
    padding: theme.spacing(2),
    borderRadius: theme.spacing(1)
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 400
  }
}));

function DeadPlantDashboard(props) {
  const classes = useStyles();
  const [loading, setLoading] = useState(true);
  const [plants, setPlants] = useState([]);
  const [stats, setStats] = useState({
    totalDead: 0,
    totalDying: 0,
    pendingRemoval: 0,
    completed: 0,
    todayReported: 0,
    weekReported: 0
  });
  const [selectedPlant, setSelectedPlant] = useState(null);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [filters, setFilters] = useState({
    dateRange: { start: null, end: null },
    type: 'all',
    status: 'all',
    crew: 'all',
    client: 'all',
    jobSite: 'all'
  });

  // Fetch plant tracking data
  const fetchPlantData = async () => {
    try {
      setLoading(true);
      
      // Build query params matching backend expectations
      const params = {};
      
      // Date range
      if (filters.dateRange.start) {
        params['dateRange.start'] = filters.dateRange.start.toISOString();
      }
      if (filters.dateRange.end) {
        params['dateRange.end'] = filters.dateRange.end.toISOString();
      }
      
      // Type filter
      if (filters.type !== 'all') {
        params.type = filters.type;
      }
      
      // Status filter - backend expects 'status' not 'state'
      if (filters.status !== 'all') {
        params.status = filters.status;
      }
      
      // Other filters
      if (filters.crew !== 'all') params.crew = filters.crew;
      if (filters.client !== 'all') params.client = filters.client;
      if (filters.jobSite !== 'all') params.jobSite = filters.jobSite;
      
      // Default pagination
      params.limit = 100;
      params.page = 1;
      
      const response = await axios.get(`${props.url}/api/hmi/plant-tracking`, {
        params
      });
      console.log('177', response)
      if (response.data.success) {
        console.log('Fetched plants:', response.data.data);
        setPlants(response.data.data || []);
        calculateStats(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching plant data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Calculate statistics from plant data
  const calculateStats = (plantData) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);

    const stats = {
      totalDead: plantData.filter(p => p.type === 'dead').length,
      totalDying: plantData.filter(p => p.type === 'dying').length,
      pendingRemoval: plantData.filter(p => p.state !== 'completed' && p.state !== 'removed').length,
      completed: plantData.filter(p => p.state === 'completed').length,
      todayReported: plantData.filter(p => new Date(p.timestamps?.created) >= today).length,
      weekReported: plantData.filter(p => new Date(p.timestamps?.created) >= weekAgo).length
    };

    setStats(stats);
  };

  useEffect(() => {
    fetchPlantData();
  }, [filters]);

  // Socket.io integration for real-time updates
  useEffect(() => {
    // Listen for plant events
    socket.on('plant:created', (data) => {
      console.log('Plant created:', data);
      // Add the new plant to the list
      setPlants(prevPlants => [data.plant, ...prevPlants]);
      calculateStats([data.plant, ...plants]);
    });

    socket.on('plant:updated', (data) => {
      console.log('Plant updated:', data);
      // Update the plant in the list
      setPlants(prevPlants => 
        prevPlants.map(plant => 
          plant._id === data.plantId ? { ...plant, ...data.updates } : plant
        )
      );
      // Recalculate stats
      const updatedPlants = plants.map(plant => 
        plant._id === data.plantId ? { ...plant, ...data.updates } : plant
      );
      calculateStats(updatedPlants);
    });

    socket.on('plant:deleted', (data) => {
      console.log('Plant deleted:', data);
      // Remove the plant from the list
      setPlants(prevPlants => prevPlants.filter(plant => plant._id !== data.plantId));
      calculateStats(plants.filter(plant => plant._id !== data.plantId));
    });

    // Cleanup listeners on unmount
    return () => {
      socket.off('plant:created');
      socket.off('plant:updated');
      socket.off('plant:deleted');
    };
  }, [plants]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
  };

  const handlePlantClick = (plant) => {
    setSelectedPlant(plant);
    setDetailsModalOpen(true);
  };

  const handleDetailsClose = () => {
    setDetailsModalOpen(false);
    setSelectedPlant(null);
  };

  const handlePlantUpdate = async (updatedPlant) => {
    await fetchPlantData();
    handleDetailsClose();
  };

  const renderStatCards = () => (
    <Grid container spacing={3}>
      <Grid item xs={12} sm={6} md={3}>
        <Paper className={`${classes.statCard} ${classes.deadPlantCard}`}>
          <LocalFlorist className={classes.statIcon} />
          <Typography className={classes.statValue}>{stats.totalDead}</Typography>
          <Typography className={classes.statLabel}>Dead Plants</Typography>
        </Paper>
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <Paper className={`${classes.statCard} ${classes.dyingPlantCard}`}>
          <Warning className={classes.statIcon} />
          <Typography className={classes.statValue}>{stats.totalDying}</Typography>
          <Typography className={classes.statLabel}>Dying Plants</Typography>
        </Paper>
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <Paper className={`${classes.statCard} ${classes.pendingCard}`}>
          <Schedule className={classes.statIcon} />
          <Typography className={classes.statValue}>{stats.pendingRemoval}</Typography>
          <Typography className={classes.statLabel}>Pending Removal</Typography>
        </Paper>
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <Paper className={`${classes.statCard} ${classes.completedCard}`}>
          <CheckCircle className={classes.statIcon} />
          <Typography className={classes.statValue}>{stats.completed}</Typography>
          <Typography className={classes.statLabel}>Completed</Typography>
        </Paper>
      </Grid>
    </Grid>
  );

  const renderContent = () => {
    if (loading) {
      return (
        <Box className={classes.loadingContainer}>
          <CircularProgress />
        </Box>
      );
    }

    switch (activeTab) {
      case 0:
        return (
          <PlantTrackingTable 
            plants={plants}
            onPlantClick={handlePlantClick}
            loading={loading}
            apiUrl={props.url}
          />
        );
      case 1:
        return (
          <PlantTrackingMap 
            plants={plants}
            onPlantClick={handlePlantClick}
          />
        );
      case 2:
        return (
          <PlantPhotoGallery 
            plants={plants}
            onPlantClick={handlePlantClick}
          />
        );
      case 3:
        return (
          <PlantAnalytics 
            plants={plants}
            stats={stats}
          />
        );
      default:
        return null;
    }
  };

  return (
    <MuiPickersUtilsProvider utils={DateFnsUtils}>
      <Container maxWidth="xl" className={classes.root}>
        <Grid container spacing={3}>
        <Grid item xs={12}>
          <Typography variant="h4" gutterBottom>
            Dead/Dying Plant Tracking Dashboard
          </Typography>
        </Grid>

        {/* Statistics Cards */}
        <Grid item xs={12}>
          {renderStatCards()}
        </Grid>

        {/* Filters */}
        <Grid item xs={12}>
          <Paper className={classes.filterContainer}>
            <PlantFilters 
              filters={filters}
              onFilterChange={handleFilterChange}
              apiUrl={props.url}
            />
          </Paper>
        </Grid>

        {/* Tabs */}
        <Grid item xs={12}>
          <Paper className={classes.tabsContainer}>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              indicatorColor="primary"
              textColor="primary"
              variant="fullWidth"
            >
              <Tab label="Table View" />
              <Tab label="Map View" />
              <Tab label="Photo Gallery" />
              <Tab label="Analytics" />
            </Tabs>
          </Paper>
        </Grid>

        {/* Content Area */}
        <Grid item xs={12}>
          <Paper>
            {renderContent()}
          </Paper>
        </Grid>
      </Grid>

      {/* Plant Details Modal */}
      {selectedPlant && (
        <PlantDetailsModal
          open={detailsModalOpen}
          onClose={handleDetailsClose}
          plant={selectedPlant}
          onUpdate={handlePlantUpdate}
          apiUrl={props.url}
        />
      )}
      </Container>
    </MuiPickersUtilsProvider>
  );
}

const mapStateToProps = (state) => ({
  user: state.auth?.user || state.user,
  url: state.api?.url || state.url || ''
});

export default connect(mapStateToProps)(DeadPlantDashboard);